import { TILES } from '../types/GameTypes'

interface Decoration {
  x: number
  y: number
  type: string
}

interface Obstacle {
  x: number
  y: number
  type: string
}

export interface MapData {
  ground: number[][]
  decorations: Decoration[]
  obstacles: Obstacle[]
}

export interface TownMapConfig {
  tileSize: number
  mapWidth: number
  mapHeight: number
}

/**
 * TownMap - Generates a medieval town map without monsters
 *
 * Layout (30x30):
 * - Central plaza with well
 * - 4 small houses around the edges
 * - Market stalls near center
 * - Trees surrounding the town
 * - Torches for atmosphere
 */
export class TownMap {
  private tileSize: number
  private mapWidth: number
  private mapHeight: number

  constructor(config: TownMapConfig) {
    this.tileSize = config.tileSize
    this.mapWidth = config.mapWidth
    this.mapHeight = config.mapHeight
  }

  /**
   * Generate the complete town map data
   */
  generate(): MapData {
    const ground: number[][] = []
    const decorations: Decoration[] = []
    const obstacles: Obstacle[] = []

    // Initialize with grass
    for (let y = 0; y < this.mapHeight; y++) {
      const row: number[] = []
      for (let x = 0; x < this.mapWidth; x++) {
        // Grass with variation
        const variation = Math.random()
        if (variation < 0.6) {
          row.push(TILES.GRASS1)
        } else if (variation < 0.85) {
          row.push(TILES.GRASS2)
        } else if (variation < 0.95) {
          row.push(TILES.GRASS3)
        } else {
          row.push(TILES.GRASS_FLOWER)
        }
      }
      ground.push(row)
    }

    // Create town boundaries (tree border)
    this.addTreeBorder(decorations)

    // Create cobblestone plaza in center
    this.createPlaza(ground)

    // Add central well
    obstacles.push({ x: 15, y: 15, type: 'well' })

    // Add houses at corners
    this.addHouses(ground, obstacles)

    // Add market stalls
    this.addMarketStalls(obstacles, decorations)

    // Add torches for lighting
    this.addTorches(decorations)

    // Add decorative elements (barrels, crates)
    this.addDecorations(obstacles)

    return { ground, decorations, obstacles }
  }

  /**
   * Add tree border around the town
   */
  private addTreeBorder(decorations: Decoration[]): void {
    // Top and bottom borders
    for (let x = 0; x < this.mapWidth; x += 2) {
      // Top row
      decorations.push({
        x: x * this.tileSize + this.tileSize / 2,
        y: this.tileSize / 2,
        type: Math.random() < 0.5 ? 'tree' : 'tree-variation'
      })
      // Bottom row
      decorations.push({
        x: x * this.tileSize + this.tileSize / 2,
        y: (this.mapHeight - 1) * this.tileSize + this.tileSize / 2,
        type: Math.random() < 0.5 ? 'tree' : 'tree-variation'
      })
    }

    // Left and right borders (skip corners)
    for (let y = 2; y < this.mapHeight - 2; y += 2) {
      // Left column
      decorations.push({
        x: this.tileSize / 2,
        y: y * this.tileSize + this.tileSize / 2,
        type: Math.random() < 0.5 ? 'tree' : 'tree-variation'
      })
      // Right column
      decorations.push({
        x: (this.mapWidth - 1) * this.tileSize + this.tileSize / 2,
        y: y * this.tileSize + this.tileSize / 2,
        type: Math.random() < 0.5 ? 'tree' : 'tree-variation'
      })
    }

    // Add some scattered trees in corners
    const cornerTrees = [
      { x: 2, y: 2 }, { x: 3, y: 3 },
      { x: this.mapWidth - 3, y: 2 }, { x: this.mapWidth - 4, y: 3 },
      { x: 2, y: this.mapHeight - 3 }, { x: 3, y: this.mapHeight - 4 },
      { x: this.mapWidth - 3, y: this.mapHeight - 3 }, { x: this.mapWidth - 4, y: this.mapHeight - 4 }
    ]
    cornerTrees.forEach(pos => {
      decorations.push({
        x: pos.x * this.tileSize + this.tileSize / 2,
        y: pos.y * this.tileSize + this.tileSize / 2,
        type: Math.random() < 0.5 ? 'tree' : 'tree-variation'
      })
    })
  }

  /**
   * Create the central cobblestone plaza
   */
  private createPlaza(ground: number[][]): void {
    const centerX = Math.floor(this.mapWidth / 2)
    const centerY = Math.floor(this.mapHeight / 2)
    const plazaRadius = 6

    for (let y = centerY - plazaRadius; y <= centerY + plazaRadius; y++) {
      for (let x = centerX - plazaRadius; x <= centerX + plazaRadius; x++) {
        if (y >= 0 && y < this.mapHeight && x >= 0 && x < this.mapWidth) {
          // Create cobblestone with slight variation
          ground[y][x] = Math.random() < 0.8 ? TILES.COBBLE : TILES.STONE_PATH
        }
      }
    }

    // Add paths leading out from plaza
    this.createPaths(ground, centerX, centerY)
  }

  /**
   * Create paths from plaza to edges
   */
  private createPaths(ground: number[][], centerX: number, centerY: number): void {
    // Path to top
    for (let y = 0; y < centerY - 6; y++) {
      ground[y][centerX] = TILES.STONE_PATH
      ground[y][centerX + 1] = TILES.STONE_PATH
    }
    // Path to bottom
    for (let y = centerY + 7; y < this.mapHeight; y++) {
      ground[y][centerX] = TILES.STONE_PATH
      ground[y][centerX + 1] = TILES.STONE_PATH
    }
    // Path to left
    for (let x = 0; x < centerX - 6; x++) {
      ground[centerY][x] = TILES.STONE_PATH
      ground[centerY + 1][x] = TILES.STONE_PATH
    }
    // Path to right
    for (let x = centerX + 7; x < this.mapWidth; x++) {
      ground[centerY][x] = TILES.STONE_PATH
      ground[centerY + 1][x] = TILES.STONE_PATH
    }
  }

  /**
   * Add houses in corners of the town
   */
  private addHouses(ground: number[][], obstacles: Obstacle[]): void {
    const housePositions = [
      { x: 5, y: 5 },   // Top-left
      { x: 23, y: 5 },  // Top-right
      { x: 5, y: 23 },  // Bottom-left
      { x: 23, y: 23 }  // Bottom-right
    ]

    housePositions.forEach(pos => {
      // Create house floor (2x2 tiles)
      for (let dy = 0; dy < 2; dy++) {
        for (let dx = 0; dx < 2; dx++) {
          if (pos.y + dy < this.mapHeight && pos.x + dx < this.mapWidth) {
            ground[pos.y + dy][pos.x + dx] = TILES.FLOOR
          }
        }
      }

      // Add building collision (entire 2x2 area)
      obstacles.push({ x: pos.x, y: pos.y, type: 'building' })
      obstacles.push({ x: pos.x + 1, y: pos.y, type: 'building' })
      obstacles.push({ x: pos.x, y: pos.y + 1, type: 'building' })
      obstacles.push({ x: pos.x + 1, y: pos.y + 1, type: 'building' })
    })
  }

  /**
   * Add market stalls near the center
   */
  private addMarketStalls(obstacles: Obstacle[], decorations: Decoration[]): void {
    const centerX = Math.floor(this.mapWidth / 2)
    const centerY = Math.floor(this.mapHeight / 2)

    // Market stall positions (around the well but not too close)
    const stallPositions = [
      { x: centerX - 4, y: centerY - 3 },
      { x: centerX + 3, y: centerY - 3 },
      { x: centerX - 4, y: centerY + 3 },
      { x: centerX + 3, y: centerY + 3 }
    ]

    stallPositions.forEach(pos => {
      // Add crate as market stall representation
      obstacles.push({ x: pos.x, y: pos.y, type: 'crate' })

      // Add barrel next to stall
      decorations.push({
        x: (pos.x + 1) * this.tileSize + this.tileSize / 2,
        y: pos.y * this.tileSize + this.tileSize / 2,
        type: 'barrel'
      })
    })
  }

  /**
   * Add torches around the town for atmosphere
   */
  private addTorches(decorations: Decoration[]): void {
    const centerX = Math.floor(this.mapWidth / 2)
    const centerY = Math.floor(this.mapHeight / 2)

    // Torches around the plaza
    const torchPositions = [
      { x: centerX - 5, y: centerY - 5 },
      { x: centerX + 5, y: centerY - 5 },
      { x: centerX - 5, y: centerY + 5 },
      { x: centerX + 5, y: centerY + 5 },
      // Along the paths
      { x: centerX, y: centerY - 8 },
      { x: centerX, y: centerY + 8 },
      { x: centerX - 8, y: centerY },
      { x: centerX + 8, y: centerY }
    ]

    torchPositions.forEach(pos => {
      decorations.push({
        x: pos.x * this.tileSize + this.tileSize / 2,
        y: pos.y * this.tileSize + this.tileSize / 2,
        type: 'torch'
      })
    })
  }

  /**
   * Add decorative elements around the town
   */
  private addDecorations(obstacles: Obstacle[]): void {
    // Barrels and crates near houses
    const decorPositions = [
      { x: 7, y: 5, type: 'barrel' },
      { x: 5, y: 7, type: 'crate' },
      { x: 25, y: 5, type: 'barrel' },
      { x: 23, y: 7, type: 'crate' },
      { x: 7, y: 23, type: 'barrel' },
      { x: 5, y: 25, type: 'crate' },
      { x: 25, y: 23, type: 'barrel' },
      { x: 23, y: 25, type: 'crate' }
    ]

    decorPositions.forEach(pos => {
      obstacles.push({ x: pos.x, y: pos.y, type: pos.type })
    })
  }

  /**
   * Get player spawn position (center of town)
   */
  getSpawnPosition(): { x: number; y: number } {
    return {
      x: Math.floor(this.mapWidth / 2) * this.tileSize + this.tileSize / 2,
      y: Math.floor(this.mapHeight / 2) * this.tileSize + this.tileSize * 1.5
    }
  }
}
